import React from 'react';
import { 
  Plane,
  Clock,
  MapPin,
  Calendar,
  User,
  TriangleAlert,
  CheckCircle2,
  QrCode,
  Mail,
  Phone
} from 'lucide-react';

const BoardingPassTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // Extract airport codes
  const getAirportCode = (text) => {
    if (!text) return '';
    // Try to extract 3-letter airport code
    const match = text.match(/\b[A-Z]{3}\b/);
    return match ? match[0] : text.substring(0, 3).toUpperCase();
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.lastName.toUpperCase()}, ${quote.client.firstName.toUpperCase()}`;
    }
    return 'PASSENGER';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Minimal Header - No Hero */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Flight Itinerary</div>
              <h1 className="text-2xl font-bold text-gray-900">{quote.quoteTitle || 'Your Flight Details'}</h1>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Booking Ref</div>
              <div className="font-mono text-sm font-bold text-gray-900">{quote.quoteNumber}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Passenger Info Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Passenger:</span>
              <span className="font-mono text-sm font-semibold text-gray-900">{getClientName()}</span>
            </div>
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Total Travelers:</span>
                <span className="font-semibold text-gray-900">
                  {quote.travelers.adults || 0} + {quote.travelers.children?.length || 0}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Boarding Passes */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {quote.components && quote.components.length > 0 ? (
          <div className="space-y-6">
            {quote.components.map((component, index) => {
              // Extract flight details
              const departureCity = component.data?.departure_city || component.data?.origin || '';
              const arrivalCity = component.data?.arrival_city || component.data?.destination || '';
              const departureCode = getAirportCode(departureCity);
              const arrivalCode = getAirportCode(arrivalCity);
              const departureTime = component.data?.departure_time || '';
              const arrivalTime = component.data?.arrival_time || '';
              const flightDate = component.data?.date || component.data?.departure_date || '';
              const airline = component.data?.airline || '';
              const flightNumber = component.data?.flight_number || '';
              const terminal = component.data?.terminal || '';
              const gate = component.data?.gate || '';
              const layoverDuration = component.data?.layover_duration || component.data?.layover || '';
              
              // Check for long layover (>4 hours)
              const isLongLayover = layoverDuration && (
                layoverDuration.includes('5h') || layoverDuration.includes('6h') || 
                layoverDuration.includes('7h') || layoverDuration.includes('8h') ||
                parseInt(layoverDuration) > 4
              );

              return (
                <React.Fragment key={component.id || index}>
                  {/* Boarding Pass Card */}
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    {/* Header Strip */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Plane className="w-5 h-5 text-white" />
                        <span className="text-white font-semibold uppercase tracking-wide">
                          {airline || 'Flight'}
                        </span>
                      </div>
                      {flightNumber && (
                        <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded text-white font-mono text-sm">
                          {flightNumber}
                        </span>
                      )}
                    </div>

                    {/* Main Boarding Pass Content */}
                    <div className="p-6">
                      {/* Airport Codes and Route */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-center">
                          <div className="text-5xl font-black text-gray-900 mb-2">{departureCode}</div>
                          <div className="text-sm text-gray-600">{departureCity}</div>
                        </div>
                        
                        <div className="flex-1 mx-8 relative">
                          <div className="border-t-2 border-dashed border-gray-300 relative">
                            <Plane className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-blue-600 bg-white px-1" />
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-5xl font-black text-gray-900 mb-2">{arrivalCode}</div>
                          <div className="text-sm text-gray-600">{arrivalCity}</div>
                        </div>
                      </div>

                      {/* Flight Details Grid */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        {/* Date */}
                        {flightDate && (
                          <div className="text-center p-3 bg-gray-50 rounded">
                            <Calendar className="w-4 h-4 text-gray-500 mx-auto mb-2" />
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</div>
                            <div className="font-mono text-sm font-semibold text-gray-900">{formatDate(flightDate)}</div>
                          </div>
                        )}

                        {/* Departure Time */}
                        {departureTime && (
                          <div className="text-center p-3 bg-gray-50 rounded">
                            <Clock className="w-4 h-4 text-gray-500 mx-auto mb-2" />
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Departs</div>
                            <div className="font-mono text-lg font-bold text-gray-900">{formatTime(departureTime)}</div>
                          </div>
                        )}

                        {/* Arrival Time */}
                        {arrivalTime && (
                          <div className="text-center p-3 bg-gray-50 rounded">
                            <Clock className="w-4 h-4 text-gray-500 mx-auto mb-2" />
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Arrives</div>
                            <div className="font-mono text-lg font-bold text-gray-900">{formatTime(arrivalTime)}</div>
                          </div>
                        )}

                        {/* Terminal/Gate */}
                        {(terminal || gate) && (
                          <div className="text-center p-3 bg-gray-50 rounded">
                            <MapPin className="w-4 h-4 text-gray-500 mx-auto mb-2" />
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Gate</div>
                            <div className="font-mono text-lg font-bold text-gray-900">
                              {terminal && `T${terminal}`}{gate && `-${gate}`}
                              {!terminal && !gate && 'TBA'}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Additional Flight Info */}
                      {component.data && Object.keys(component.data).length > 0 && (
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || 
                                key === 'isOption' || 
                                key === 'optionDescription' ||
                                key === 'departure_city' ||
                                key === 'arrival_city' ||
                                key === 'origin' ||
                                key === 'destination' ||
                                key === 'departure_time' ||
                                key === 'arrival_time' ||
                                key === 'date' ||
                                key === 'departure_date' ||
                                key === 'airline' ||
                                key === 'flight_number' ||
                                key === 'terminal' ||
                                key === 'gate') {
                              return null;
                            }
                            
                            // Check if this is a price field
                            const isPriceField = key.toLowerCase().includes('price') || 
                                                 key.toLowerCase().includes('cost') || 
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            
                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }
                            
                            return (
                              <div key={key} className="flex justify-between p-2 bg-gray-50 rounded">
                                <span className="text-gray-500 uppercase tracking-wide">{key.replace(/_/g, ' ')}:</span>
                                <span className="font-semibold text-gray-900">{displayValue}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Price */}
                      <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-gray-600 font-semibold">Flight Cost</span>
                        <span className="text-2xl font-bold text-gray-900">{formatCurrency(component.price)}</span>
                      </div>

                      {/* Component Photos */}
                      {component.photos && component.photos.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                            <span>📸</span> Photos
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {component.photos.map((photo, photoIndex) => (
                              <div 
                                key={photoIndex} 
                                className="relative group cursor-pointer rounded overflow-hidden shadow hover:shadow-md transition-shadow border-2 border-blue-200 bg-white"
                                onClick={() => window.open(photo.url, '_blank')}
                              >
                                <img 
                                  src={photo.url} 
                                  alt={photo.name || 'Component photo'} 
                                  className="w-full h-auto object-contain"
                                  style={{ maxHeight: '300px' }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1">
                                  <span className="text-white text-xs font-semibold">{photo.name || 'View'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Component Documents */}
                      {component.documents && component.documents.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide flex items-center gap-2">
                            <span>📋</span> Documents
                          </h4>
                          <div className="space-y-1">
                            {component.documents.map((doc, docIndex) => (
                              <a 
                                key={docIndex}
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded transition-colors border border-gray-200 text-sm"
                              >
                                <span>📄</span>
                                <span className="text-gray-900 font-medium">{doc.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Barcode Footer */}
                    <div className="bg-gray-50 px-6 py-4 border-t-2 border-dashed border-gray-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <QrCode className="w-8 h-8 text-gray-400" />
                          <div className="text-xs text-gray-500">
                            <div className="font-mono font-semibold text-gray-900">{quote.quoteNumber}</div>
                            <div>Scan at airport kiosk</div>
                          </div>
                        </div>
                        <div className="font-mono text-xs text-gray-400">
                          BOARDING PASS {index + 1} OF {quote.components.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Layover Warning */}
                  {layoverDuration && index < quote.components.length - 1 && (
                    <div className={`flex items-center justify-center gap-3 py-4 px-6 rounded-lg ${
                      isLongLayover ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'
                    }`}>
                      {isLongLayover ? (
                        <TriangleAlert className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-blue-600" />
                      )}
                      <span className={`font-semibold ${isLongLayover ? 'text-amber-900' : 'text-blue-900'}`}>
                        Layover: {layoverDuration}
                      </span>
                      {isLongLayover && (
                        <span className="text-amber-700 text-sm">(Extended layover - plan accordingly)</span>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <Plane className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No flights added to this itinerary</p>
          </div>
        )}

        {/* Notes */}
        {quote.notes && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <TriangleAlert className="w-5 h-5" />
              Important Travel Information
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Total Investment */}
        <div className="mt-8 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
          <div className="bg-gray-900 px-6 py-4">
            <h3 className="text-white font-semibold uppercase tracking-wide">Total Flight Cost</h3>
          </div>
          
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <span className="text-gray-600 text-lg">Total Investment</span>
              <span className="text-4xl font-bold text-gray-900">{formatCurrency(quote.totalPrice)}</span>
            </div>

            {quote.depositAmount > 0 && (
              <div className="pt-6 border-t border-gray-200 grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Deposit Required</div>
                  <div className="text-2xl font-bold text-gray-900">{formatCurrency(quote.depositAmount)}</div>
                </div>
                {quote.depositDueDate && (
                  <div className="bg-gray-50 rounded p-4">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Due Date</div>
                    <div className="text-xl font-semibold text-gray-900">{formatDate(quote.depositDueDate)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Button */}
            {onPayment && (
              <button
                onClick={onPayment}
                disabled={isAgentPreview}
                className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-3 ${
                  isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'
                }`}
              >
                <CheckCircle2 className="w-5 h-5" />
                {isAgentPreview ? 'Preview Mode' : 'Confirm Booking'}
              </button>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-8 bg-white rounded-lg shadow-md border-2 border-blue-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-3">
              <div className="flex items-center justify-center gap-2 text-white">
                <Plane className="w-5 h-5" />
                <h3 className="text-base font-bold uppercase tracking-wide">Your Travel Agent</h3>
              </div>
            </div>
            
            <div className="p-5">
              {quote.agent_name && (
                <div className="text-center mb-3">
                  <p className="text-lg font-bold text-gray-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-gray-600 font-semibold text-sm">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-2 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-gray-50 px-4 py-2.5 rounded hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <Mail className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-900 font-medium text-sm">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-gray-50 px-4 py-2.5 rounded hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <Phone className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-900 font-medium text-sm">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardingPassTemplate;
